import React from 'react';
import { IonFooter, IonModal, IonToolbar } from '@ionic/react';
import HkModalContent from 'components/modal/HkModalContent';
import { slugifyModalTitle } from 'components/modal/HkModalHeader';
import './HkModal.scss';

interface HkModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  className?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Standalone modal (Figma header/content/footer shape shared across the
 * Delete Confirm, Analyze Image, and Add Item modals) — owns its own
 * IonModal. `className` is appended alongside the shared `hk-modal` class so
 * each usage can still override sizing/content specifics (e.g.
 * `.hk-inventory-delete-confirm-modal .hk-modal-content { padding: ...; }`).
 *
 * `backdropDismiss={false}`: these are all confirm/action-style modals —
 * dismissing on an accidental backdrop click (rather than the explicit
 * Cancel/close affordances) isn't wanted for any of them.
 *
 * Footer renders here (an `IonFooter`/`IonToolbar`, matching the
 * `IonHeader`/`IonToolbar` header pairing), as a true sibling of
 * `HkModalContent` rather than nested inside it — same "only content
 * scrolls, header/footer stay pinned" shape InventoryAddProduct.tsx uses.
 * IonToolbar exposes no `::part()` for restyling its internal flex
 * container, so the actual visual chrome (#f7f7f7 background,
 * flex-end-aligned buttons) still lives on the inner `.hk-modal-footer` div
 * — IonToolbar/IonFooter here are structural only (zeroed padding/min-height
 * via `.hk-modal-footer-toolbar` in HkModal.scss), not restyled directly.
 */
const HkModal: React.FC<HkModalProps> = ({
  isOpen,
  title,
  onClose,
  className,
  footer,
  children
}) => {
  return (
    <IonModal
      className={`hk-modal${className ? ` ${className}` : ''}`}
      isOpen={isOpen}
      backdropDismiss={false}
      onDidDismiss={onClose}
      aria-labelledby={slugifyModalTitle(title)}
    >
      <HkModalContent title={title} onClose={onClose}>
        {children}
      </HkModalContent>
      {footer && (
        // `ion-no-border` drops Ionic's default footer drop-shadow (the
        // Figma frames have a flat #f7f7f7 footer, no shadow).
        <IonFooter className="ion-no-border">
          <IonToolbar className="hk-modal-footer-toolbar">
            <div className="hk-modal-footer">{footer}</div>
          </IonToolbar>
        </IonFooter>
      )}
    </IonModal>
  );
};

export default HkModal;
